<template>
  <div
    class="scale-degree-label"
    :class="[
      `scale-degree-label--${size}`,
      {
        'scale-degree-label--accidental': hasAccidental,
      },
    ]"
  >
    <!-- Custom SVG accidental symbols come FIRST for scale degrees -->
    <svg
      v-if="isSharp"
      class="degree-accidental degree-accidental--sharp"
      :width="getAccidentalSize().width"
      :height="getAccidentalSize().height"
      :viewBox="svgPaths.sharp.viewBox"
    >
      <path
        v-for="(path, index) in svgPaths.sharp.paths"
        :key="index"
        :d="path.d"
        fill="currentColor"
      />
    </svg>
    <svg
      v-if="isFlat"
      class="degree-accidental degree-accidental--flat"
      :width="getAccidentalSize().width"
      :height="getAccidentalSize().height"
      :viewBox="svgPaths.flat.viewBox"
    >
      <path
        v-for="(path, index) in svgPaths.flat.paths"
        :key="index"
        :d="path.d"
        fill="currentColor"
      />
    </svg>
    <span class="degree-number">{{ degreeNumber }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults, defineProps } from "vue";
import { getChromaticScaleDegree } from "@/utils/noteUtils";
import { svgPaths } from "@/utils/svgPaths";
import type { Scale } from "@/types";

interface Props {
  chromaticInterval: number;
  scale?: Scale;
  size?: "small" | "medium" | "large";
}

const props = withDefaults(defineProps<Props>(), {
  size: "medium",
});

const scaleDegree = computed(() => {
  // Handle Locrian mode special case: ♯4 → ♭5
  if (props.scale?.name === "Locrian" && props.chromaticInterval === 6) {
    return "♭5";
  }
  
  return getChromaticScaleDegree(props.chromaticInterval);
});

const degreeNumber = computed(() => {
  return scaleDegree.value.replace(/[♯♭#b]/g, '');
});

const hasAccidental = computed(() => {
  return scaleDegree.value.includes('♯') || scaleDegree.value.includes('#') || 
         scaleDegree.value.includes('♭') || scaleDegree.value.includes('b');
});

const isSharp = computed(() => {
  return scaleDegree.value.includes('♯') || scaleDegree.value.includes('#');
});

const isFlat = computed(() => {
  return scaleDegree.value.includes('♭') || scaleDegree.value.includes('b');
});

// Dynamic sizing for SVG accidental symbols based on component size
const getAccidentalSize = computed(() => () => {
  switch (props.size) {
    case "small":
      return { width: 7, height: 13 }; // ~70% of original
    case "large":
      return { width: 9, height: 17 }; // ~130% of original
    case "medium":
    default:
      return { width: 7, height: 13 }; // Original size
  }
});
</script>

<style scoped lang="scss">
.scale-degree-label {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  font-family: "Jost", sans-serif;
  font-weight: 600;
  text-align: center;
  color: inherit;

  // Size variants
  &--small {
    .degree-number {
      font-size: 14px;
      line-height: 1;
    }
    .degree-accidental {
      margin-right: 1px;
      align-self: flex-start;
      margin-top: 2px;
    }
  }

  &--medium {
    .degree-number {
      font-size: 18px;
      line-height: 1;
    }
    .degree-accidental {
      margin-right: 1px;
      align-self: flex-start;
      margin-top: 2px;
    }
  }

  &--large {
    .degree-number {
      font-size: 24px;
      line-height: 1;
    }
    .degree-accidental {
      margin-right: 3px;
      align-self: flex-start;
      margin-top: -1px;
    }
  }
}

.degree-number {
  font-family: "Jost", sans-serif;
  font-weight: 600;
  color: inherit;
}

.degree-accidental {
  font-family: "Jost", sans-serif;
  font-weight: 400;
  position: relative;
  top: -0.1em;
  color: inherit;

  svg {
    color: inherit;
  }

  &--sharp {
    // Additional styling for sharp symbols if needed
  }

  &--flat {
    // Additional styling for flat symbols if needed
  }
}
</style>